// Shared helper functions for parsing different types of uploaded Excel files.

import type { WorkSheet } from 'xlsx'

declare const XLSX: any

/**
 * Extracts the period (YYYY-MM) from a standard template file's "B2" cell.
 */
export function getPeriodFromFile(worksheet: WorkSheet): string {
  const periodCell = worksheet['B2']
  if (!periodCell || !periodCell.v) {
    throw new Error('Period not found in cell B2. Please use the template and fill in the period.')
  }
  return parsePeriodCell(periodCell.v, periodCell.t, 'B2')
}

/**
 * Extracts the period (YYYY-MM) from a Sales Data file's "B5" cell.
 */
export function getPeriodFromSalesData(worksheet: WorkSheet): string {
  const periodCell = worksheet['B5']
  if (!periodCell || !periodCell.v) {
    // If B5 is empty, we might be looking at a raw CSV or different format.
    // We will let the range parsers (getPeriodRangeFromEsbData) handle it later if needed,
    // but for the Modal validation, we usually expect B5 in ESB templates.
    // For auto-detection, we often rely on the 'Sales Date In' column logic below.
    return '' 
  }

  const cellValue = periodCell.v.toString()
  const startDateString = cellValue.split(' - ')[0]

  if (!startDateString) {
    throw new Error(`Invalid date range format in cell B5: "${cellValue}".`)
  }

  const parts = startDateString.split('-')
  if (parts.length === 3) {
    const month = parts[1]
    const year = parts[2]
    if (/^\d{4}$/.test(year) && /^\d{2}$/.test(month)) {
      return `${year}-${month}`
    }
  }

  return parsePeriodCell(startDateString, 's', 'B5')
}

/**
 * [NEW] Scans the worksheet to find the Branch Name.
 * Handles ESB (Column 'Branch') and Moka (Column 'Outlet').
 */
export function detectBranchNameFromSheet(worksheet: WorkSheet, format: 'ESB' | 'MOKA'): string {
  // Convert first few rows to JSON to find headers and first data row
  const json = XLSX.utils.sheet_to_json(worksheet, { 
    range: format === 'ESB' ? 10 : 0, // ESB headers usually at row 11 (index 10), Moka at 0
    header: 0 // Auto-detect headers
  });

  if (json.length === 0) {
    throw new Error('File appears to be empty.');
  }

  // Look at the first data row
  const firstRow: any = json[0]; 

  if (format === 'ESB') {
    if (firstRow['Branch']) return String(firstRow['Branch']).trim();
    // Fallback: sometimes ESB exports might vary, check B1 or similar if needed, 
    // but standard ESB has 'Branch' column.
  } 
  else if (format === 'MOKA') {
    if (firstRow['Outlet']) return String(firstRow['Outlet']).trim();
  }

  // If we couldn't find it in the data columns, try checking metadata cells
  // Some reports put Branch Name in B1 or B3
  const b1 = worksheet['B1'];
  if (b1 && b1.v) return String(b1.v).trim();

  throw new Error(`Could not auto-detect Branch Name. Please ensure the file has a '${format === 'ESB' ? 'Branch' : 'Outlet'}' column.`);
}

function parsePeriodCell(cellValue: any, cellType: string, cellRef: string): string {
  if (cellType === 'n' && typeof cellValue === 'number') {
    const date = convertExcelDate(cellValue)
    if (date && !isNaN(date.getTime())) {
      const year = date.getFullYear()
      const month = (date.getMonth() + 1).toString().padStart(2, '0')
      return `${year}-${month}`
    }
  }

  if (typeof cellValue === 'string') {
    const dateString = cellValue.trim()
    const matchYYYYMM = dateString.match(/^(\d{4})[-](\d{1,2})$/)
    if (matchYYYYMM) {
      const year = matchYYYYMM[1]
      const month = matchYYYYMM[2].padStart(2, '0')
      return `${year}-${month}`
    }
    const matchDDMMYYYY = dateString.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/)
    if (matchDDMMYYYY) {
      const month = matchDDMMYYYY[2].padStart(2, '0')
      const year = matchDDMMYYYY[3]
      return `${year}-${month}`
    }
    const date = new Date(dateString)
    if (date && !isNaN(date.getTime())) {
      const year = date.getFullYear()
      const month = (date.getMonth() + 1).toString().padStart(2, '0')
      return `${year}-${month}`
    }
  }
  throw new Error(`Could not understand date format in ${cellRef}.`)
}

export function getPeriodRangeFromEsbData(worksheet: WorkSheet): { startPeriod: string, endPeriod: string } {
  const jsonRows: any[] = XLSX.utils.sheet_to_json(worksheet, { range: 11 })
  if (jsonRows.length === 0) throw new Error('ESB file has no data rows.')

  let earliestDate: Date | null = null
  let latestDate: Date | null = null

  jsonRows.forEach((row) => {
    const dateValue = row['Sales Date In']
    if (dateValue === undefined || dateValue === null) return

    let currentDate: Date | null = null

    if (typeof dateValue === 'number') {
      currentDate = convertExcelDate(dateValue)
    } else if (typeof dateValue === 'string') {
      const parts = dateValue.includes('/') ? dateValue.split('/') : dateValue.split('-')
      if (parts.length === 3) {
        const part1 = parseInt(parts[0], 10)
        const part2 = parseInt(parts[1], 10)
        let year = parseInt(parts[2], 10)
        if (year < 100) year += 2000

        if (part1 > 12) { 
          currentDate = new Date(year, part2 - 1, part1)
        } else { 
          currentDate = new Date(year, part1 - 1, part2)
        }
      }
    }

    if (currentDate && !isNaN(currentDate.getTime())) {
      if (!earliestDate || currentDate < earliestDate) earliestDate = currentDate
      if (!latestDate || currentDate > latestDate) latestDate = currentDate
    }
  })

  if (!earliestDate || !latestDate) {
    throw new Error('Could not find any valid dates in the \'Sales Date In\' column.')
  }

  const startPeriod = `${earliestDate.getFullYear()}-${(earliestDate.getMonth() + 1).toString().padStart(2, '0')}`
  const endPeriod = `${latestDate.getFullYear()}-${(latestDate.getMonth() + 1).toString().padStart(2, '0')}`

  return { startPeriod, endPeriod }
}

export function getPeriodRangeFromMokaData(worksheet: WorkSheet): { startPeriod: string, endPeriod: string } {
  const json = XLSX.utils.sheet_to_json(worksheet, { header: 1 })
  if (json.length < 2) throw new Error('Moka file is empty or has no data rows.')

  const headers = json[0] as string[]
  const dateIndex = headers.findIndex((h) => h === 'Date')
  if (dateIndex === -1) throw new Error('Column \'Date\' not found in Moka file.')

  let earliestDate: Date | null = null
  let latestDate: Date | null = null

  for (let i = 1; i < json.length; i++) {
    const row = json[i] as any[]
    const dateString = row[dateIndex]

    if (dateString && typeof dateString === 'string') {
      const parts = dateString.split('-')
      if (parts.length === 3 && parts[0] && parts[1] && parts[2]) {
        const day = parseInt(parts[0], 10)
        const month = parseInt(parts[1], 10) - 1 
        const year = parseInt(parts[2], 10)

        if (!isNaN(day) && !isNaN(month) && !isNaN(year)) {
          const currentDate = new Date(year, month, day)
          if (!earliestDate || currentDate < earliestDate) earliestDate = currentDate
          if (!latestDate || currentDate > latestDate) latestDate = currentDate
        }
      }
    }
  }

  if (!earliestDate || !latestDate) {
    throw new Error('Could not find any valid dates in the \'Date\' column.')
  }

  const earliest = earliestDate as Date
  const latest = latestDate as Date

  const startPeriod = `${earliest.getFullYear()}-${(earliest.getMonth() + 1).toString().padStart(2, '0')}`
  const endPeriod = `${latest.getFullYear()}-${(latest.getMonth() + 1).toString().padStart(2, '0')}`

  return { startPeriod, endPeriod }
}

function convertExcelDate(serial: number): Date | null {
  if (isNaN(serial) || serial < 0) {
    return null
  }
  const excelToUnixEpochDays = 25569
  const unixTimestampMilliseconds = (serial - excelToUnixEpochDays) * 86400 * 1000
  const date = new Date(unixTimestampMilliseconds)
  const adjustedDate = new Date(date.getTime() + (date.getTimezoneOffset() * 60 * 1000))
  return !isNaN(adjustedDate.getTime()) ? adjustedDate : null
}